import React, { memo, useState, useEffect, FC, useMemo, useRef } from "react";
import { Check, Download, Play, X, Zap, SlidersHorizontal, Info, Loader2, Cloud, CloudOff, Share2, Trash2, Package } from "lucide-react";
import { AspectRatio } from "../../media-recorder-exporter";
import { EXPORT_FORMATS, COMPRESSION_LEVELS } from "./constants";
import { ChunkStatus } from "../../client-merge";
import { DetailedExportProgress } from "./DetailedExportProgress";
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import ExportAlphaDialog from './ExportAlphaDialog';

interface ExportTabProps {
    isExporting?: boolean;
    exportStatus?: string;
    exportProgress?: number;
    exportDetails?: { id: number; type: string; duration: number; effects: string[] }[];
    exportedVideos?: { id: string; name: string; urlWebM?: string; urlMP4?: string; thumbnailUrl?: string; mp4ConversionProgress?: number; srtContent?: string; }[];
    onStartExport?: (options: { mode: string; format: string; compression: string; extension: string; codec: string; }) => void;
    onCancelExport?: () => void;
    exportAspectRatio: AspectRatio;
    onExportAspectRatioChange: (ratio: AspectRatio) => void;
    isTimelineEligibleForFastExport: boolean;
    vfxSegmentPreviews?: { id: string; name: string; blobUrl: string; duration: number }[];
    // Server export props
    onStartServerExport?: (options?: {
        burnSubtitles?: boolean;
        quality?: 'draft' | 'standard' | 'high';
        mode?: 'chunk' | 'full';
        useGPU?: boolean;
        engine?: 'mlt' | 'ffmpeg';
    }) => void;
    activeServerExports?: {
        isActive: boolean;
        jobId?: string;
        status?: 'pending' | 'downloading' | 'processing' | 'encoding' | 'uploading' | 'complete' | 'failed';
        progress?: number;
        currentStep?: string;
        downloadUrl?: string;
        error?: string;
    }[];
    onCancelServerExport?: (jobId?: string) => void;
    onDeleteExport?: (videoId: string, videoUrl: string) => void;
    projectName?: string;
    // Client-side chunk manager
    chunks?: ChunkStatus[];
    isMerging?: boolean;
    onRetryChunk?: (chunkIndex: number) => void;
    // Hybrid export
    onStartHybridExport?: () => void;
    hasTimelineOverlays?: boolean;
    totalTimelineDuration?: number;
}

const ExportTab: FC<ExportTabProps> = ({
    isExporting = false,
    exportStatus = "Not exporting",
    exportProgress = 0,
    exportDetails = [],
    exportedVideos = [],
    // FIX: Update default prop to match function signature and prevent type error.
    onStartExport = (options: { mode: string; format: string; compression: string; extension: string; codec: string; burnSubtitles?: boolean }) => { },
    onCancelExport = () => { },
    exportAspectRatio,
    onExportAspectRatioChange,
    isTimelineEligibleForFastExport,
    vfxSegmentPreviews = [],
    // Server export
    onStartServerExport,
    activeServerExports = [],
    onCancelServerExport,
    onDeleteExport,
    projectName = "Projet",
    // Chunk manager
    chunks = [],
    isMerging = false,
    onRetryChunk,
    onStartHybridExport,
    hasTimelineOverlays = false,
    totalTimelineDuration = 0,
}) => {
    const [showAlphaDialog, setShowAlphaDialog] = useState(false);

    const formatEta = (ms: number): string => {
        const totalSeconds = Math.max(0, Math.round(ms / 1000));
        if (totalSeconds < 60) return `${totalSeconds}s`;
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        if (minutes < 60) return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
        const hours = Math.floor(minutes / 60);
        const remMinutes = minutes % 60;
        return `${hours}h ${String(remMinutes).padStart(2, '0')}m`;
    };

    const estimateRemainingMs = (startedAt: number | null | undefined, progress: number | undefined): number | null => {
        if (!startedAt) return null;
        const p = Math.max(0, Math.min(100, Math.round(progress || 0)));
        if (p < 5 || p >= 100) return null; // Too early or complete

        const elapsedMs = Date.now() - startedAt;
        if (elapsedMs < 4000) return null; // Avoid noisy estimate in first seconds

        const remaining = (elapsedMs * (100 - p)) / p;
        if (!Number.isFinite(remaining) || remaining < 0) return null;
        return remaining;
    };

    const [previewingVideo, setPreviewingVideo] = useState<{ id: string, name: string, url: string, srtContent?: string } | null>(null);
    const [burnSubtitles, setBurnSubtitles] = useState(true);
    const [exportQuality, setExportQuality] = useState<'draft' | 'standard' | 'high'>('standard');
    const [exportMode, setExportMode] = useState<'chunk' | 'full'>('full');
    const localExportStartedAtRef = useRef<number | null>(null);
    const serverExportStartedAtRef = useRef<Record<string, number>>({});
    const [, setEtaTick] = useState(0);

    // Convertir SRT en WebVTT et créer un blob URL
    const [vttBlobUrl, setVttBlobUrl] = useState<string | null>(null);
    const visibleServerExports = useMemo(() => {
        const exportedIds = new Set(exportedVideos.map(v => v.id).filter(Boolean));
        const exportedUrls = new Set(
            exportedVideos
                .flatMap(v => [v.urlMP4, v.urlWebM])
                .filter((url): url is string => Boolean(url))
        );

        return activeServerExports.filter((job) => {
            const alreadyListedInCompleted = job.status === 'complete' && (
                (!!job.jobId && exportedIds.has(job.jobId)) ||
                (!!job.downloadUrl && exportedUrls.has(job.downloadUrl))
            );

            if (alreadyListedInCompleted) return false;
            return job.isActive || job.status === 'complete';
        });
    }, [activeServerExports, exportedVideos]);

    // Refresh ETA every second
    useEffect(() => {
        const timer = window.setInterval(() => setEtaTick((t) => t + 1), 1000);
        return () => window.clearInterval(timer);
    }, []);

    // Track local export start for ETA
    useEffect(() => {
        if (isExporting && exportProgress > 0 && exportProgress < 100) {
            if (!localExportStartedAtRef.current) {
                localExportStartedAtRef.current = Date.now();
            }
            return;
        }

        if (!isExporting || exportProgress >= 100 || exportProgress <= 0) {
            localExportStartedAtRef.current = null;
        }
    }, [isExporting, exportProgress]);

    // Track each server export start for ETA
    useEffect(() => {
        const now = Date.now();
        const activeKeys = new Set<string>();

        activeServerExports.forEach((job, index) => {
            const key = job.jobId || `temp-${index}`;
            activeKeys.add(key);
            const p = job.progress || 0;
            const isDone = job.status === 'complete' || job.status === 'failed';

            if (!isDone && p > 0 && p < 100 && !serverExportStartedAtRef.current[key]) {
                serverExportStartedAtRef.current[key] = now;
            }

            if (isDone || p >= 100) {
                delete serverExportStartedAtRef.current[key];
            }
        });

        Object.keys(serverExportStartedAtRef.current).forEach((k) => {
            if (!activeKeys.has(k)) delete serverExportStartedAtRef.current[k];
        });
    }, [activeServerExports]);

    useEffect(() => {
        if (!previewingVideo?.srtContent) {
            setVttBlobUrl(null);
            return;
        }

        // Convertir SRT en WebVTT (simple conversion)
        const vttContent = 'WEBVTT\n\n' + previewingVideo.srtContent.replace(/(\d{2}):(\d{2}):(\d{2}),(\d{3})/g, '$1:$2:$3.$4');
        const blob = new Blob([vttContent], { type: 'text/vtt' });
        const url = URL.createObjectURL(blob);

        setVttBlobUrl(url);

        return () => {
            // Cleanup on unmount or when previewingVideo changes
            URL.revokeObjectURL(url);
        };
    }, [previewingVideo?.srtContent]);

    const handleDownloadZip = async (video: any, dateString: string) => {
        const zip = new JSZip();
        // Sanitize project name
        const safeProjectName = projectName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
        const fileNameBase = `${safeProjectName}_${dateString}`;

        try {
            // 1. Add Video
            const videoUrl = video.urlMP4 || video.urlWebM;
            if (videoUrl) {
                const response = await fetch(videoUrl);
                const blob = await response.blob();
                const ext = video.urlMP4 ? 'mp4' : 'webm';
                zip.file(`${fileNameBase}.${ext}`, blob);
            }

            // 2. Add Subtitles (if available)
            if (video.srtContent) {
                zip.file(`${fileNameBase}.srt`, video.srtContent);
            }

            // 3. Generate and Save
            const content = await zip.generateAsync({ type: "blob" });
            saveAs(content, `${fileNameBase}.zip`);
        } catch (error) {
            console.error("Failed to zip:", error);
            alert("Erreur lors de la création du ZIP. Vérifiez votre connexion.");
        }
    };

    const clickExport = () => {
        const DURATION_THRESHOLD = 300; // 5 minutes

        // PRIORITY 1: If timeline has overlays/effects → always server (WebCodecs local can't render overlays)
        if (hasTimelineOverlays) {
            if (onStartHybridExport) {
                onStartHybridExport();
                return;
            }
            if (onStartServerExport) {
                onStartServerExport({ burnSubtitles: true, quality: 'standard' });
                return;
            }
        }

        // PRIORITY 2: Short video without effects (< 5 min) → Full local WebCodecs (instant)
        if (totalTimelineDuration < DURATION_THRESHOLD) {
            if (!onStartExport) return;
            onStartExport({ mode: 'soft', format: 'mp4', compression: 'none', extension: '.mp4', codec: 'copy', burnSubtitles });
            return;
        }

        // PRIORITY 3: Long video without effects (≥ 5 min) → Constance (Hybrid chunked Web+Cloud)
        if (onStartHybridExport) {
            onStartHybridExport();
            return;
        } else if (onStartServerExport) {
            onStartServerExport({ burnSubtitles: true, quality: 'standard' });
            return;
        }

        // Fallback: local export
        if (!onStartExport) return;
        onStartExport({ mode: 'soft', format: 'mp4', compression: 'none', extension: '.mp4', codec: 'copy', burnSubtitles });
    };

    return (
        <>
            <div id="panel-export" role="tabpanel" aria-labelledby="tab-export" className="h-full flex flex-col">
                <div className="px-4 py-3 border-b border-gray-100">
                    <h2 className="text-sm font-semibold text-gray-900">Export</h2>
                    <p className="text-xs text-gray-500">Gérez vos exports vidéo</p>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-6">
                    {isExporting && visibleServerExports.length === 0 ? (
                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <h3 className="font-semibold text-xs uppercase tracking-wide text-gray-500">Export en cours</h3>
                                <button
                                    onClick={onCancelExport}
                                    className="p-1.5 rounded-full hover:bg-red-100 text-red-600 transition-colors"
                                    title="Annuler l'export"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                                <div className="flex justify-between items-center mb-1"><span className="text-sm font-medium text-gray-600">{exportStatus}</span><span className="text-sm font-medium text-gray-500">{Math.round(exportProgress)}%</span></div>
                                <div className="w-full bg-gray-200 rounded-full h-2 mb-4"><div className="bg-black h-2 rounded-full transition-all" style={{ width: `${exportProgress}%` }}></div></div>
                                {(() => {
                                    const etaMs = estimateRemainingMs(localExportStartedAtRef.current, exportProgress);
                                    if (!etaMs) return null;
                                    return (
                                        <div className="text-xs text-gray-500 -mt-2 mb-3">
                                            Temps restant estimé: ~{formatEta(etaMs)}
                                        </div>
                                    );
                                })()}

                                {exportDetails && exportDetails.length > 0 && (
                                    <div className="mt-4 space-y-2 max-h-40 overflow-y-auto pr-1 custom-scrollbar">
                                        <h4 className="text-xs font-semibold text-gray-500 mb-2">Détails des segments :</h4>
                                        {exportDetails.map((segment, idx) => (
                                            <div key={idx} className="flex items-center gap-2 p-2 bg-white rounded-lg border border-gray-100 text-xs">
                                                <div className={`w-2 h-2 rounded-full shrink-0 ${segment.type === 'prerender' ? 'bg-orange-500' : 'bg-green-500'}`} />
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex justify-between items-center mb-0.5">
                                                        <span className="font-medium text-gray-700">Segment {idx + 1}</span>
                                                        <span className="text-gray-400">{Math.round(segment.duration)}s</span>
                                                    </div>
                                                    <div className="text-gray-500 flex flex-wrap gap-1">
                                                        {segment.type === 'prerender' ? (
                                                            <>
                                                                <span className="text-orange-600 font-medium">Rendu (VFX)</span>
                                                                {segment.effects.length > 0 && (
                                                                    <span className="text-gray-400">• {segment.effects.join(', ')}</span>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <span className="text-green-600 font-medium">Copie (Ultra-rapide)</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* VFX Segment Previews */}
                                {vfxSegmentPreviews && vfxSegmentPreviews.length > 0 && (
                                    <div className="mt-4 space-y-2 max-h-40 overflow-y-auto pr-1 custom-scrollbar">
                                        <h4 className="text-xs font-semibold text-gray-500 mb-2">Clips VFX générés :</h4>
                                        {vfxSegmentPreviews.map((seg) => (
                                            <div key={seg.id} className="flex items-center gap-2 p-2 bg-white rounded-lg border border-gray-100 text-xs">
                                                <button
                                                    onClick={() => setPreviewingVideo({ id: seg.id, name: seg.name, url: seg.blobUrl })}
                                                    className="w-8 h-8 rounded-full bg-purple-600 hover:bg-purple-700 flex items-center justify-center text-white shrink-0 transition-colors"
                                                    title="Prévisualiser"
                                                >
                                                    <Play className="w-4 h-4" />
                                                </button>
                                                <div className="flex-1 min-w-0">
                                                    <span className="font-medium text-gray-700 truncate block">{seg.name}</span>
                                                    <span className="text-gray-400">{seg.duration.toFixed(1)}s</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Detailed Chunk Visualization (Smart Hybrid) */}
                            {chunks && chunks.length > 0 && isExporting && (
                                <div className="mt-4">
                                    <DetailedExportProgress
                                        chunks={chunks}
                                        totalDuration={totalTimelineDuration}
                                        isMerging={isMerging || exportStatus.includes('Assemblage')}
                                        onRetryChunk={onRetryChunk}
                                    />
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {/* Burn-in Toggle */}
                            <label htmlFor="burnSubtitles" className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors">
                                <input
                                    type="checkbox"
                                    id="burnSubtitles"
                                    checked={burnSubtitles}
                                    onChange={(e) => setBurnSubtitles(e.target.checked)}
                                    className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black"
                                />
                                <div className="flex-1">
                                    <span className="text-sm font-medium text-gray-700">Incruster les sous-titres</span>
                                    <span className="block text-[10px] text-gray-500 mt-0.5">
                                        {burnSubtitles ? 'Gravés dans l\'image' : 'Piste séparée (désactivable)'}
                                    </span>
                                </div>
                            </label>

                            {/* Export Button */}
                            {(() => {
                                const isBusy = isExporting || activeServerExports.some(j => j.isActive && j.status !== 'complete' && j.status !== 'failed');
                                const noVideo = !totalTimelineDuration || totalTimelineDuration === 0;
                                const isDisabled = isBusy || noVideo;
                                const isCloud = totalTimelineDuration > 300;
                                return (
                                    <div className="space-y-2">
                                        <button
                                            onClick={clickExport}
                                            disabled={isDisabled}
                                            className={`w-full px-4 py-4 font-bold transition-all flex items-center justify-center gap-3 shadow-lg text-base rounded-lg ${isDisabled
                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                : 'bg-black hover:bg-gray-900 text-white'
                                                }`}
                                        >
                                            <Cloud className="w-5 h-5" />
                                            {isBusy
                                                ? 'Export en cours...'
                                                : noVideo
                                                    ? 'Aucune vidéo chargée'
                                                    : 'Exporter la vidéo'
                                            }
                                            {!isDisabled && isCloud && (
                                                <span className="text-[10px] opacity-50 ml-1">
                                                    {hasTimelineOverlays ? 'Hybride' : 'Cloud'}
                                                </span>
                                            )}
                                        </button>

                                        {/* Export Alpha Button — Opens clean export dialog */}
                                        <button
                                            onClick={() => setShowAlphaDialog(true)}
                                            disabled={noVideo}
                                            className={`w-full px-4 py-3 font-bold transition-all flex items-center justify-center gap-2 border-2 text-sm rounded-lg ${noVideo
                                                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                                : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white border-transparent shadow-md hover:shadow-lg'
                                                }`}
                                            title="Export WebCodecs avec dialog propre (qualité, preview)"
                                        >
                                            <Zap className="w-4 h-4" />
                                            Export (Alpha)
                                            <span className="text-[10px] opacity-70 ml-1">WebCodecs</span>
                                        </button>

                                        {/* MLT Export Button */}
                                        <button
                                            onClick={() => {
                                                if (onStartServerExport) {
                                                    onStartServerExport({ burnSubtitles: true, quality: 'standard', engine: 'mlt' });
                                                }
                                            }}
                                            disabled={isDisabled}
                                            className={`w-full px-4 py-3 font-semibold transition-all flex items-center justify-center gap-2 border text-sm rounded-lg ${isDisabled
                                                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                                : 'bg-white hover:bg-gray-50 text-indigo-600 border-indigo-200 hover:border-indigo-300'
                                                }`}
                                            title="Expérimental: Utilise MLT Framework pour un meilleur rendu des effets"
                                        >
                                            <Package className="w-4 h-4" />
                                            Export Cloud MLT (Bêta)
                                        </button>

                                        <button
                                            onClick={() => {
                                                if (onStartServerExport) {
                                                    onStartServerExport({
                                                        burnSubtitles: true,
                                                        quality: 'standard',
                                                        mode: 'chunk',
                                                        engine: 'mlt',
                                                    });
                                                }
                                            }}
                                            disabled={isDisabled}
                                            className={`w-full px-4 py-3 font-semibold transition-all flex items-center justify-center gap-2 border text-sm rounded-lg ${isDisabled
                                                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                                : 'bg-white hover:bg-gray-50 text-blue-700 border-blue-200 hover:border-blue-300'
                                                }`}
                                            title="Turbo MLT parallèle"
                                        >
                                            <Zap className="w-4 h-4" />
                                            Turbo MLT parallèle
                                        </button>
                                    </div>
                                );
                            })()}
                        </div>
                    )}

                    {/* Active Server Exports — Constance-style visualization */}
                    {visibleServerExports.length > 0 && (
                        <div className="space-y-4 mt-4">
                            {visibleServerExports.map((job, index) => {
                                const jobKey = job.jobId || `temp-${index}`;
                                const etaMs = estimateRemainingMs(serverExportStartedAtRef.current[jobKey], job.progress);
                                // Synthesize chunks for DetailedExportProgress
                                // If job has chunks from Firestore, use them (background jobs)
                                // Fallback to global chunks (active session)
                                const actualChunks = (job.chunks && job.chunks.length > 0) ? job.chunks : chunks;
                                const displayChunks: (ChunkStatus & { mode?: 'local' | 'server' })[] = actualChunks.length > 0
                                    ? actualChunks.map(c => ({ ...c, mode: (c as any).mode || 'server' }))
                                    : [{
                                        index: 0,
                                        mode: 'server' as const,
                                        serverStatus: (
                                            job.status === 'complete' ? 'complete' :
                                                job.status === 'failed' ? 'failed' :
                                                    job.status === 'encoding' ? 'encoding' :
                                                        job.status === 'downloading' ? 'downloading' :
                                                            (job.status === 'processing' || job.status === 'uploading') ? 'encoding' :
                                                                'pending'
                                        ) as ChunkStatus['serverStatus'],
                                        serverProgress: job.progress || 0,
                                        url: job.downloadUrl || null,
                                        localStatus: (
                                            job.status === 'complete' ? 'downloaded' : 'waiting'
                                        ) as ChunkStatus['localStatus'],
                                        localProgress: job.status === 'complete' ? 100 : 0,
                                        size: 0,
                                    }];

                                return (
                                    <div key={jobKey} className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                                        {/* Header with cancel */}
                                        <div className="flex justify-between items-center mb-2">
                                            <div className="flex items-center gap-2">
                                                {job.status === 'complete' ? (
                                                    <Cloud className="w-4 h-4 text-green-600" />
                                                ) : (
                                                    <Loader2 className="w-4 h-4 text-purple-500 animate-spin" />
                                                )}
                                                <span className="text-sm font-medium text-gray-800">
                                                    Export Cloud {job.status === 'pending' ? '(En attente)' : ''}
                                                </span>
                                            </div>
                                            {onCancelServerExport && job.status !== 'complete' && (
                                                <button
                                                    onClick={() => onCancelServerExport(job.jobId)}
                                                    className="p-1.5 rounded-full hover:bg-red-100 text-red-600 transition-colors"
                                                    title="Annuler"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>

                                        {/* Progress visualization */}
                                        {actualChunks.length > 0 ? (
                                            <>
                                                <DetailedExportProgress
                                                    chunks={displayChunks}
                                                    totalDuration={totalTimelineDuration}
                                                    isMerging={isMerging}
                                                    onRetryChunk={onRetryChunk}
                                                />
                                                {/* Step label below */}
                                                {job.currentStep && job.status !== 'complete' && job.status !== 'failed' && (
                                                    <div className="mt-2 text-xs text-gray-500 font-mono truncate">
                                                        {job.currentStep}
                                                    </div>
                                                )}
                                                {etaMs && job.status !== 'complete' && job.status !== 'failed' && (
                                                    <div className="mt-1 text-xs text-blue-700">
                                                        Temps restant estimé: ~{formatEta(etaMs)}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="my-3 space-y-2">
                                                <div className="flex justify-between items-center mb-1">
                                                    <span className="text-sm text-blue-700 truncate max-w-[200px]">
                                                        {job.currentStep || job.status}
                                                    </span>
                                                    <span className="text-sm font-bold text-blue-600">
                                                        {(job.progress || 0) > 0 ? `${job.progress}%` : <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />}
                                                    </span>
                                                </div>
                                                <div className="w-full bg-blue-200 rounded-full h-3 mb-2 overflow-hidden">
                                                    <div
                                                        className="bg-gradient-to-r from-blue-500 to-cyan-500 h-3 rounded-full transition-all duration-300 flex items-center justify-center"
                                                        style={{ width: `${Math.max(job.progress || 0, 5)}%` }}
                                                    >
                                                        {(job.progress || 0) >= 15 && (
                                                            <span className="text-[10px] font-bold text-white drop-shadow">{job.progress}%</span>
                                                        )}
                                                    </div>
                                                </div>
                                                {etaMs && job.status !== 'complete' && job.status !== 'failed' && (
                                                    <div className="text-xs text-blue-700">
                                                        Temps restant estimé: ~{formatEta(etaMs)}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Download button */}
                                        {job.status === 'complete' && job.downloadUrl && (
                                            <a
                                                href={job.downloadUrl}
                                                download
                                                className="w-full mt-3 bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-lg font-medium transition-all flex items-center justify-center gap-2"
                                            >
                                                <Download className="w-4 h-4" />
                                                Télécharger
                                            </a>
                                        )}

                                        {/* Error state */}
                                        {job.status === 'failed' && (
                                            <div className="mt-2 p-2 bg-red-100 rounded-lg text-xs text-red-700">
                                                Erreur: {job.error || 'Export échoué'}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="pt-4 mt-4 border-t border-gray-200">
                        <h3 className="font-semibold text-xs uppercase tracking-wide text-gray-500 mb-3">Exports terminés</h3>
                        {exportedVideos.length === 0 ? (<div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed"><p className="text-sm text-gray-500">Vos vidéos exportées apparaîtront ici.</p></div>) : (<ul className="space-y-3">{exportedVideos.map(video => {
                            // Extract date and time
                            const dateTimeMatch = video.name.match(/(\d{4}-\d{2}-\d{2})_(\d{2})h(\d{2})/);
                            const dateMatch = video.name.match(/(\d{4}-\d{2}-\d{2})/);
                            let displayDate: string;
                            let dateStringForFile: string;

                            if (dateTimeMatch) {
                                const [, date, hour, min] = dateTimeMatch;
                                const formattedDate = new Date(date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
                                displayDate = `${formattedDate} à ${hour}h${min}`;
                                dateStringForFile = `${date}_${hour}h${min}`;
                            } else if (dateMatch) {
                                displayDate = new Date(dateMatch[1]).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
                                dateStringForFile = dateMatch[1];
                            } else {
                                displayDate = video.name;
                                dateStringForFile = new Date().toISOString().slice(0, 10);
                            }

                            const safeProjectName = projectName.replace(/[^a-z0-9]/gi, '_').toLowerCase();

                            return (
                                <li key={video.id} className="w-full flex items-center gap-3 p-2 rounded-xl border border-gray-200 bg-white">
                                    <button onClick={() => setPreviewingVideo({ id: video.id, name: video.name, url: video.urlMP4 || video.urlWebM || '', srtContent: video.srtContent })} className="w-16 h-10 rounded-lg bg-gray-900 flex items-center justify-center shrink-0 relative group overflow-hidden">
                                        {video.thumbnailUrl ? (
                                            <img src={video.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <span className="text-lg">📹</span>
                                        )}
                                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Play className="w-5 h-5 text-white" />
                                        </div>
                                    </button>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-800 truncate" title={projectName}>{projectName}</p>
                                        <p className="text-xs text-gray-500">{displayDate}</p>
                                    </div>
                                    <div className="flex gap-1.5">
                                        {video.urlMP4 && (
                                            <a
                                                href={video.urlMP4}
                                                download={`${safeProjectName}_${dateStringForFile}.mp4`}
                                                className="flex items-center gap-1 px-2.5 py-1.5 text-white bg-black hover:bg-gray-800 rounded-lg transition-all text-xs font-medium"
                                                title="Télécharger MP4"
                                            >
                                                <Download className="w-3 h-3" />
                                                <span className="hidden sm:inline">MP4</span>
                                            </a>
                                        )}

                                        {/* ZIP Package Button */}
                                        {(video.urlMP4 || video.urlWebM) && (
                                            <button
                                                onClick={() => handleDownloadZip(video, dateStringForFile)}
                                                className="flex items-center gap-1 px-2 py-1.5 text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-all"
                                                title="Télécharger ZIP (Vidéo + Sous-titres)"
                                            >
                                                <Package className="w-3.5 h-3.5" />
                                            </button>
                                        )}

                                        {/* Share button */}
                                        {(video.urlMP4 || video.urlWebM) && (
                                            <button
                                                onClick={() => {
                                                    const url = video.urlMP4 || video.urlWebM;
                                                    if (!url) {
                                                        alert('Aucun lien disponible');
                                                        return;
                                                    }
                                                    navigator.clipboard.writeText(url)
                                                        .then(() => alert('Lien copié !'))
                                                        .catch(err => {
                                                            console.error('Failed to copy', err);
                                                            alert('Impossible de copier le lien. Vérifiez les permissions de votre navigateur.');
                                                        });
                                                }}
                                                className="flex items-center px-2 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all"
                                                title="Copier le lien"
                                            >
                                                <Share2 className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                        {/* Delete button */}
                                        {onDeleteExport && (video.urlMP4 || video.urlWebM) && (
                                            <button
                                                onClick={() => {
                                                    if (confirm('Supprimer cet export ?')) {
                                                        onDeleteExport(video.id, video.urlMP4 || video.urlWebM || '');
                                                    }
                                                }}
                                                className="flex items-center px-2 py-1.5 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-all"
                                                title="Supprimer"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>
                                </li>
                            );
                        })}</ul>)}
                    </div>
                </div>
            </div >
            {previewingVideo && (
                <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => setPreviewingVideo(null)}>
                    <div className="bg-gray-900 rounded-xl shadow-2xl w-full max-w-4xl flex flex-col border border-gray-700" onClick={e => e.stopPropagation()}>
                        <div className="p-4 border-b border-gray-700 flex justify-between items-center">
                            <h3 className="text-lg font-semibold text-white truncate">{previewingVideo.name}</h3>
                            <button onClick={() => setPreviewingVideo(null)} className="p-1 rounded-full hover:bg-gray-700 text-gray-300">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-4 bg-black">
                            <video src={previewingVideo.url} controls autoPlay className="w-full max-h-[75vh] rounded-lg" crossOrigin="anonymous">
                            </video>
                        </div>
                    </div>
                </div>
            )
            }
            {/* Alpha Export Dialog */}
            <ExportAlphaDialog
                open={showAlphaDialog}
                onClose={() => setShowAlphaDialog(false)}
                onStartExport={onStartExport}
                onCancelExport={onCancelExport}
                isExporting={isExporting}
                exportProgress={exportProgress}
                exportStatus={exportStatus}
                exportedVideos={exportedVideos}
                totalTimelineDuration={totalTimelineDuration}
            />
        </>
    );
};

const MemoizedExportTab = memo(ExportTab);
MemoizedExportTab.displayName = 'ExportTab';

export default MemoizedExportTab;
